import { collection, query, where } from "firebase/firestore";
import { useMemo } from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import { useAuth } from "../contexts/AuthContext";
import { POST_ANALYTICS_COLLECTION, PostAnalytics } from "../models/PostAnalytics";
import { db } from "../services/Firebase";

interface UsePostAnalyticsResult {
  /** connectionId → analytics record for this post (server-written). */
  byConnectionId: Record<string, PostAnalytics>;
  loading: boolean;
  error?: Error;
}

/**
 * Live per-publication engagement analytics for one post. One record per
 * platform connection the post published to; written exclusively by the
 * server's metrics sweep.
 */
export function usePostAnalytics(wavegenPostId: string | null | undefined): UsePostAnalyticsResult {
  const { currentUser, activeWorkspaceId } = useAuth();

  const [snapshot, loading, error] = useCollection(
    currentUser && activeWorkspaceId && wavegenPostId
      ? query(
          collection(db, POST_ANALYTICS_COLLECTION),
          // userId remains the compatibility tenant alias; its value is the
          // workspace id on both legacy and workspace-aware analytics docs.
          where("userId", "==", activeWorkspaceId),
          where("wavegenPostId", "==", wavegenPostId)
        )
      : null
  );

  const byConnectionId = useMemo(() => {
    const map: Record<string, PostAnalytics> = {};
    for (const doc of snapshot?.docs ?? []) {
      const data = { ...(doc.data() as Omit<PostAnalytics, "id">), id: doc.id } as PostAnalytics;
      if (data.userId !== activeWorkspaceId) continue;
      map[data.connectionId] = data;
    }
    return map;
  }, [activeWorkspaceId, snapshot]);

  return { byConnectionId, loading, error };
}
